import { isPlainObject } from 'lodash/fp';
import { useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { AbortError, SendRequestOptions } from '@mews/framework';
import { DangerouslySetHtmlContent, ErrorHandler, OverlayedLoader } from '@mews-commander/ui';
import { useAppConfiguration } from '@mews-commander/data-access';
import { PlatformEnvironment } from '@mews/enums';
import {
    createUrlFromPath,
    getCurrentUrl,
    getPathFromUrl,
    useCommanderSelector,
    useCommanderThunkDispatch,
} from '@mews-commander/utils';
import { useCurrentEnterpriseId, useCurrentProfileId } from '@mews-commander/core';
import { getCurrentProfileScopeId } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import {
    META_STATE_FAILED,
    META_STATE_STARTED,
    META_STATE_SUCCEEDED,
} from '@mews-commander/legacy-entities/src/constants';
import { fetchTemplate } from '../../actions/common';
import { ModalManagerDialogEventHandler } from '../../../modalManager/ModalManagerDialogEventHandler';
import { getRouterBasePathRegex } from '../../../navigation';
import {
    ApiGeneratedAlertType,
    showAlert,
    getErrorTexts,
    getServerNormalizedUrl as baseGetServerNormalizedUrl,
    getStacktraceFromErrorTemplate,
} from './utils';

const layoutType = 'EmptyDialog';
const returnHtmlOnly = String(false);

interface DialogTemplateFetcherProps<Data> {
    url: string;
    onFetchSuccess?: (data: Data) => void;
}

export const DialogTemplateFetcher = <Data,>({ onFetchSuccess, ...props }: DialogTemplateFetcherProps<Data>) => {
    const [htmlContent, setHtmlContent] = useState('');
    const [{ isFetching, hasFailed }, setStatus] = useState(META_STATE_SUCCEEDED.meta);
    const [errorProps, setError] = useState<{
        errorMessage: ReactNode;
        errorTitle: ReactNode;
        stackTrace: string | null;
    }>({
        errorMessage: null,
        errorTitle: null,
        stackTrace: null,
    });

    const dispatch = useCommanderThunkDispatch();

    const { environment, routerBaseUrl, serverBasePath, platformPrimaryUrl } = useAppConfiguration();
    const showStackTrace = environment !== PlatformEnvironment.Production && environment !== PlatformEnvironment.Demo;

    const currentEnterpriseId = useCurrentEnterpriseId();
    const profileId = useCurrentProfileId();
    const scopeId = useCommanderSelector<string>(getCurrentProfileScopeId);
    const routerBasePathRegex = useCommanderSelector(getRouterBasePathRegex);
    const currentUrl = useCommanderSelector(getCurrentUrl);

    const fetchController = useRef(new window.AbortController());

    const getServerNormalizedUrl = useCallback(
        (url: string) =>
            baseGetServerNormalizedUrl(routerBasePathRegex, routerBaseUrl, serverBasePath, platformPrimaryUrl, url),
        [routerBasePathRegex, routerBaseUrl, serverBasePath, platformPrimaryUrl]
    );
    const referrerUrl = encodeURI(getServerNormalizedUrl(currentUrl));

    const handleFetch = useCallback(
        async ({
            url,
            options = {},
            data = undefined,
        }: {
            url: string;
            options: Partial<SendRequestOptions>;
            data?: FormData;
        }) => {
            const finalUrl = getServerNormalizedUrl(url);
            const finalOptions = {
                ...options,
                signal: fetchController.current.signal,
                credentials: 'include' as const,
                shouldReturnResponseData: true,
            };
            setStatus(META_STATE_STARTED.meta);

            try {
                const {
                    data: responseData,
                    headers,
                    redirected,
                } = await (dispatch(fetchTemplate(finalUrl, finalOptions, data)) as unknown as Promise<{
                    data: unknown;
                    headers: Headers;
                    redirected: boolean;
                }>);

                if (!responseData || isPlainObject(responseData)) {
                    onFetchSuccess?.(responseData as Data);
                    return;
                }

                if (redirected) {
                    const requestUrl = headers.get('RequestUrl') ?? '';
                    const parsedUrl = createUrlFromPath(requestUrl);
                    const { searchParams } = parsedUrl;

                    const flashType = searchParams.get('FlashType');
                    const flashMessage = searchParams.get('FlashMessage');
                    if (flashType && flashMessage) {
                        showAlert({ message: flashMessage, type: flashType as ApiGeneratedAlertType });
                    }

                    /**
                     * In Dialog, the redirect shouldn't be followed, so we just return.
                     * If the user of Dialog needs to act on it, we trigger a callback here.
                     */
                    return;
                }

                setStatus(META_STATE_SUCCEEDED.meta);
                setHtmlContent(responseData as string);
            } catch (error) {
                if (!(error instanceof AbortError)) {
                    const { data: errorData, statusCode } = error;
                    const { errorMessage, errorTitle } = getErrorTexts(statusCode);
                    const stackTrace =
                        showStackTrace && typeof errorData === 'string'
                            ? getStacktraceFromErrorTemplate(errorData)
                            : null;

                    setStatus(META_STATE_FAILED.meta);
                    setError({ errorMessage, errorTitle, stackTrace });
                }
            }
        },
        [getServerNormalizedUrl, dispatch, fetchController, showStackTrace, onFetchSuccess]
    );

    useEffect(
        () => () => {
            fetchController.current.abort();
        },
        []
    );

    useEffect(() => {
        const options = {
            method: 'GET' as const,
            accept: '*/*',
            customHeaders: {
                'X-Mews-Referer': referrerUrl,
            },
        };

        const url = createUrlFromPath(props.url);
        const { searchParams } = url;
        searchParams.set('Layout', layoutType);
        searchParams.set('ScopeId', scopeId);
        searchParams.set('ProfileId', profileId);
        searchParams.set('ReturnHtmlOnly', returnHtmlOnly);

        const enterpriseId = searchParams.get('EnterpriseId');
        if (!enterpriseId) {
            searchParams.set('EnterpriseId', currentEnterpriseId);
        }

        const finalUrl = getPathFromUrl(url);
        void handleFetch({ url: finalUrl, options });
    }, [props.url, currentEnterpriseId, profileId, scopeId, handleFetch, referrerUrl]);

    const handlePostFormSubmit = useCallback(
        ({ url, data }: { url: string; data: FormData }) => {
            const options = {
                method: 'POST' as const,
                accept: '*/*',
                autoContentType: true,
                customHeaders: {
                    'X-Mews-Referer': referrerUrl,
                },
            };

            data.append('Layout', layoutType);
            data.append('ScopeId', scopeId);
            data.append('ProfileId', profileId);
            data.append('ReturnHtmlOnly', returnHtmlOnly);

            const enterpriseId = data.get('EnterpriseId');
            if (!enterpriseId) {
                data.append('EnterpriseId', currentEnterpriseId);
            }

            void handleFetch({ url, options, data });
        },
        [currentEnterpriseId, profileId, scopeId, referrerUrl, handleFetch]
    );

    if (hasFailed) {
        return <ErrorHandler {...errorProps} />;
    }

    return (
        <>
            {isFetching ? <OverlayedLoader show /> : null}
            <ModalManagerDialogEventHandler onPostFormSubmit={handlePostFormSubmit}>
                <DangerouslySetHtmlContent html={htmlContent} />
            </ModalManagerDialogEventHandler>
        </>
    );
};
